import React, { useState, useEffect } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import { useParams, useNavigate } from 'react-router-dom';
import PublicNavigation from '../../components/PublicNavigation';
import './ChapterDetailKPI.css';

function ChapterDetailKPI() {
  const { chapterName } = useParams();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSchool, setSelectedSchool] = useState(null);

  useEffect(() => {
    loadProjects();
  }, []);

  async function loadProjects() {
    try {
      const snap = await getDocs(collection(db, 'manualProjects'));
      const data = snap.docs.map(d => ({ id: d.id, ...d.data() }))
        .filter(p => p.chapter === decodeURIComponent(chapterName));
      setProjects(data);
    } catch (error) {
      console.error('Error:', error);
    }
    setLoading(false);
  }

  const extractSchools = (beneficiaryName) => {
    if (!beneficiaryName) return [];
    return beneficiaryName.split(/[\n,]+/).map(n => n.trim()).filter(n => n.length > 0);
  };

  const getFunds = (project) => {
    if (project.totalFunds) {
      return {
        received: project.totalFunds.received || 0,
        disbursed: project.totalFunds.disbursed || 0,
        openingBalance: project.totalFunds.openingBalance || 0,
        closingBalance: project.totalFunds.closingBalance || 0
      };
    }
    return {
      received: project.fundReceived || 0,
      disbursed: project.fundDisbursed || 0,
      openingBalance: project.fundOpeningBalance || 0,
      closingBalance: project.fundClosingBalance || 0
    };
  };

  const formatINR = (amount) => {
    return `₹${Math.round(amount).toLocaleString('en-IN')}`;
  };

  const getSchoolDetails = (schoolName) => {
    const projectsWithSchool = projects.filter(p => 
      extractSchools(p.beneficiaryName).some(s => s === schoolName)
    );
    
    if (projectsWithSchool.length === 0) return null;

    const project = projectsWithSchool[0];
    
    return {
      schoolName,
      district: project.district,
      coordinator: project.projectCoordinator,
      photoLinks: project.photoLinks || [],
      projectUpdates: project.projectUpdates || [],
      category: project.category,
      beneficiaries: project.totalBeneficiaries,
      status: project.status
    };
  };

  const getMetrics = () => {
    const schools = new Set();
    const districts = new Set();
    const beneficiaryCategories = {};
    let totalBeneficiaries = 0;
    let totalFundsReceived = 0;
    let totalFundsDisbursed = 0;
    let activeProjects = 0;
    let completedProjects = 0;

    projects.forEach(p => {
      totalBeneficiaries += p.totalBeneficiaries || 0;
      
      const funds = getFunds(p);
      totalFundsReceived += funds.received;
      totalFundsDisbursed += funds.disbursed;
      
      if (p.status === 'Active') activeProjects++;
      if (p.status === 'Completed') completedProjects++;
      
      if (p.district) districts.add(p.district);
      
      extractSchools(p.beneficiaryName).forEach(school => schools.add(school));

      if (p.beneficiaryCategories) {
        Object.entries(p.beneficiaryCategories).forEach(([cat, count]) => {
          if (!beneficiaryCategories[cat]) beneficiaryCategories[cat] = 0;
          beneficiaryCategories[cat] += parseInt(count) || 0;
        });
      }
    });

    const fundsAvailable = totalFundsReceived - totalFundsDisbursed;

    return {
      totalProjects: projects.length,
      totalSchools: schools.size,
      totalBeneficiaries,
      totalFundsReceived,
      totalFundsDisbursed,
      fundsAvailable,
      activeProjects,
      completedProjects,
      districtCount: districts.size,
      beneficiaryCategories,
      schools: Array.from(schools),
      districts: Array.from(districts)
    };
  };

  const getDistrictBreakdown = () => {
    const byDistrict = {};
    projects.forEach(p => {
      const dist = p.district || 'Unknown';
      if (!byDistrict[dist]) {
        byDistrict[dist] = {
          projects: 0,
          schools: new Set(),
          beneficiaries: 0
        };
      }
      byDistrict[dist].projects++;
      byDistrict[dist].beneficiaries += p.totalBeneficiaries || 0;
      extractSchools(p.beneficiaryName).forEach(s => byDistrict[dist].schools.add(s));
    });

    return Object.entries(byDistrict).map(([name, data]) => ({
      name,
      projects: data.projects,
      schools: data.schools.size,
      beneficiaries: data.beneficiaries
    })).sort((a, b) => b.projects - a.projects);
  };

  const metrics = getMetrics();
  const districtBreakdown = getDistrictBreakdown();
  
  const categoryData = Object.entries(metrics.beneficiaryCategories)
    .sort((a, b) => a[0].localeCompare(b[0]));

  const handleSchoolClick = (schoolName) => {
    const details = getSchoolDetails(schoolName);
    setSelectedSchool(details);
  };

  if (loading) return (
    <div className="detail-page">
      <PublicNavigation />
      <div className="loading">Loading...</div>
    </div>
  );

  return (
    <div className="detail-page">
      <PublicNavigation />
      
      <div className="detail-container">
        <div className="detail-header">
          <button onClick={() => navigate('/dashboards/chapters')} className="back-btn">← Back to Chapters</button>
          <div className="header-info">
            <h1>{decodeURIComponent(chapterName)}</h1>
            <p className="subtitle">Chapter Performance KPIs & Analytics</p>
          </div>
          <div className="header-meta">
            <span className="meta-item">{metrics.districtCount} Districts</span>
            <span className="meta-item">{metrics.totalProjects} Projects</span>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="kpi-grid">
          <div className="kpi-card blue">
            <div className="kpi-icon">📁</div>
            <div className="kpi-content">
              <div className="kpi-value">{metrics.totalProjects}</div>
              <div className="kpi-label">Total Projects</div>
              <div className="kpi-sub">{metrics.activeProjects} Active • {metrics.completedProjects} Completed</div>
            </div>
          </div>

          <div className="kpi-card green">
            <div className="kpi-icon">🏫</div>
            <div className="kpi-content">
              <div className="kpi-value">{metrics.totalSchools}</div>
              <div className="kpi-label">Schools Supported</div>
              <div className="kpi-sub">Across {metrics.districtCount} districts</div>
            </div>
          </div>

          <div className="kpi-card purple">
            <div className="kpi-icon">👥</div>
            <div className="kpi-content">
              <div className="kpi-value">{metrics.totalBeneficiaries.toLocaleString('en-IN')}</div>
              <div className="kpi-label">Total Beneficiaries</div>
              <div className="kpi-sub">Students impacted</div>
            </div>
          </div>

          <div className="kpi-card orange">
            <div className="kpi-icon">💰</div>
            <div className="kpi-content">
              <div className="kpi-value">{formatINR(metrics.totalFundsReceived)}</div>
              <div className="kpi-label">Funds Received</div>
              <div className="kpi-sub">Available: {formatINR(metrics.fundsAvailable)}</div>
            </div>
          </div>
        </div>

        {/* Charts Row */}
        <div className="charts-row">
          {/* Beneficiary Categories Chart */}
          <div className="chart-card">
            <h3>Beneficiary Categories Distribution</h3>
            <div className="chart-content">
              {categoryData.length > 0 ? (
                <div className="bar-chart">
                  {categoryData.map(([cat, count]) => {
                    const maxCount = Math.max(...categoryData.map(c => c[1]));
                    const percentage = (count / maxCount) * 100;
                    return (
                      <div key={cat} className="bar-item">
                        <div className="bar-label">{cat}</div>
                        <div className="bar-container">
                          <div className="bar-fill" style={{width: `${percentage}%`}}>
                            <span className="bar-value">{count.toLocaleString('en-IN')}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="no-data">No beneficiary category data available</div>
              )}
            </div>
          </div>

          {/* District Breakdown */}
          <div className="chart-card">
            <h3>District-wise Breakdown</h3>
            <div className="chart-content">
              <table className="district-table">
                <thead>
                  <tr>
                    <th>District</th>
                    <th>Projects</th>
                    <th>Schools</th>
                    <th>Beneficiaries</th>
                  </tr>
                </thead>
                <tbody>
                  {districtBreakdown.map(d => (
                    <tr key={d.name}>
                      <td><strong>{d.name}</strong></td>
                      <td>{d.projects}</td>
                      <td>{d.schools}</td>
                      <td>{d.beneficiaries.toLocaleString('en-IN')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Schools List */}
        <div className="schools-section">
          <h3>🏫 Schools Supported ({metrics.totalSchools})</h3>
          <p className="section-note">Click on any school to view detailed information</p>
          <div className="schools-grid">
            {metrics.schools.map((school, idx) => (
              <div key={idx} className="school-item clickable" onClick={() => handleSchoolClick(school)}>
                <span className="school-number">{idx + 1}</span>
                <span className="school-name">{school}</span>
                <span className="school-arrow">→</span>
              </div>
            ))}
          </div>
        </div>

        {/* Projects List */}
        <div className="projects-section">
          <h3>📋 Project Details</h3>
          <table className="projects-table">
            <thead>
              <tr>
                <th>Category</th>
                <th>District</th>
                <th>Schools</th>
                <th>Beneficiaries</th>
                <th>Funds Received</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {projects.map(p => {
                const funds = getFunds(p);
                return (
                  <tr key={p.id}>
                    <td>{p.category}</td>
                    <td>{p.district}</td>
                    <td>{extractSchools(p.beneficiaryName).length}</td>
                    <td>{p.totalBeneficiaries || 0}</td>
                    <td>{formatINR(funds.received)}</td>
                    <td><span className={`status ${p.status?.toLowerCase()}`}>{p.status}</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* School Details Modal */}
      {selectedSchool && (
        <div className="modal" onClick={() => setSelectedSchool(null)}>
          <div className="school-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>🏫 {selectedSchool.schoolName}</h2>
              <button onClick={() => setSelectedSchool(null)} className="close-btn">×</button>
            </div>
            
            <div className="school-details">
              <div className="detail-row">
                <span className="detail-label">District:</span>
                <span className="detail-value">{selectedSchool.district}</span>
              </div>
              
              <div className="detail-row">
                <span className="detail-label">Coordinator:</span>
                <span className="detail-value">{selectedSchool.coordinator || 'Not assigned'}</span>
              </div>
              
              <div className="detail-row">
                <span className="detail-label">Category:</span>
                <span className="detail-value">{selectedSchool.category}</span>
              </div>
              
              <div className="detail-row">
                <span className="detail-label">Beneficiaries:</span>
                <span className="detail-value">{selectedSchool.beneficiaries}</span>
              </div>
              
              <div className="detail-row">
                <span className="detail-label">Status:</span>
                <span className={`status ${selectedSchool.status?.toLowerCase()}`}>{selectedSchool.status}</span>
              </div>
            </div>

            {selectedSchool.photoLinks && selectedSchool.photoLinks.length > 0 && selectedSchool.photoLinks[0].link && (
              <div className="photos-section">
                <h4>📷 Photo Links</h4>
                {selectedSchool.photoLinks.map((photo, idx) => (
                  photo.link && (
                    <div key={idx} className="photo-item">
                      <strong>{photo.title || `Photo ${idx + 1}`}:</strong>
                      <a href={photo.link} target="_blank" rel="noopener noreferrer" className="photo-link">
                        View on Google Drive →
                      </a>
                    </div>
                  )
                ))}
              </div>
            )}

            {selectedSchool.projectUpdates && selectedSchool.projectUpdates.length > 0 && selectedSchool.projectUpdates[0].update && (
              <div className="updates-section">
                <h4>📝 Project Updates</h4>
                {selectedSchool.projectUpdates.map((update, idx) => (
                  update.update && (
                    <div key={idx} className="update-item">
                      <div className="update-date">{update.date}</div>
                      <div className="update-text">{update.update}</div>
                    </div>
                  )
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default ChapterDetailKPI;
